import type {
  AutomationActionKey,
  AutomationActionValue,
  AutomationAssigneeData,
  AutomationConditionKey,
  AutomationConditionValue,
  AutomationDateConditionValue,
  AutomationSerializedValue,
  AutomationStatusData,
  AutomationTemplateData,
  AutomationValue,
} from "../../../../slices/AutomationSlice";
import {
  AUTOMATION_ACTION_KEY,
  AUTOMATION_CONDITION_KEY,
} from "../../../../slices/AutomationSlice";
import { SelectOptionType } from "../../../../types/types";
import {
  ActionConfig,
  AUTOMATION_ACTION_CONFIGS,
  AUTOMATION_CONDITION_CONFIGS,
  AutomationControl,
  AutomationDraft,
  AutomationLookupOptions,
  AutomationOptionsKey,
  AutomationScope,
  ConditionConfig,
} from "./automation.constants";
import type { AutomationStatusOption } from "./useAutomationLookups";

export const createEmptyDraft = (
  scope: AutomationScope = "work",
): AutomationDraft => ({
  scope,
  conditionValue: "",
  conditionExtraValue: "",
  actionValue: "",
  actionExtraValue: "",
});

export const createId = () => {
  if (
    typeof window !== "undefined" &&
    window.crypto &&
    typeof window.crypto.randomUUID === "function"
  ) {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random()}`;
};

export const formatDate = (date: string) => {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(new Date(date));
};

export const getSingleSelectValue = (
  value: SelectOptionType | readonly SelectOptionType[],
) => {
  if (Array.isArray(value)) {
    return value[0] ?? null;
  }

  return value;
};

export const findSelectedOption = (
  options: SelectOptionType[],
  value?: string,
): SelectOptionType | null => {
  if (!value) {
    return null;
  }

  return options.find((option) => option.value === value) ?? null;
};

export const getConditionConfig = (key?: AutomationConditionKey) => {
  return AUTOMATION_CONDITION_CONFIGS.find(
    (condition) => condition.key === key,
  );
};

export const getActionConfig = (key?: AutomationActionKey) => {
  return AUTOMATION_ACTION_CONFIGS.find((action) => action.key === key);
};

export const getControlOptionsKey = (
  control: AutomationControl,
): AutomationOptionsKey | null => {
  if (control.type === "none") {
    return null;
  }

  return control.optionsKey;
};

export const resolveOptionLabel = (
  options: AutomationLookupOptions,
  optionsKey: AutomationOptionsKey,
  value?: string,
  fallback = "",
) => {
  if (!value) {
    return fallback;
  }

  const option = options[optionsKey]?.find((item) => item.value === value);

  return option?.label ?? (fallback || value);
};

export const getStatusConditionValue = (
  options: SelectOptionType[],
  statusId?: string,
): AutomationStatusData | null => {
  if (!statusId) {
    return null;
  }

  const option = options.find((item) => item.value === statusId) as
    | AutomationStatusOption
    | undefined;

  if (option?.statusKind === "sub") {
    if (!option.mainStatusId) {
      return null;
    }

    return {
      mainStatusId: option.mainStatusId,
      subStatusId: option.subStatusId ?? statusId,
    };
  }

  return {
    mainStatusId: option?.mainStatusId ?? statusId,
    subStatusId: null,
  };
};

export const getAssigneeData = (
  userId?: string,
): AutomationAssigneeData | null => {
  return userId ? { userIds: [userId] } : null;
};

export const getTemplateData = ({
  templateId,
  emailAddress,
}: {
  templateId?: string;
  emailAddress?: string;
}): AutomationTemplateData | null => {
  if (!templateId) {
    return null;
  }

  return { emailTemplateId: templateId, emailAddress: emailAddress || null };
};

export const getDateConditionValue = (
  daysValue?: string,
  directionValue?: string,
): AutomationDateConditionValue | null => {
  const days = Number(daysValue);

  if (
    !Number.isFinite(days) ||
    days <= 0 ||
    (directionValue !== "before" && directionValue !== "after")
  ) {
    return null;
  }

  return {
    direction: directionValue,
    days,
  };
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return !!value && typeof value === "object" && !Array.isArray(value);
};

const getNullableString = (value: unknown): string | null | undefined => {
  if (typeof value === "string") {
    return value;
  }

  if (value === null) {
    return null;
  }

  return undefined;
};

const getRecordValue = (
  value: Record<string, unknown>,
  keys: string[],
): unknown => {
  const matchedKey = keys.find((key) => key in value);

  return matchedKey ? value[matchedKey] : undefined;
};

export const parseAutomationValue = (
  value: AutomationSerializedValue | undefined,
): AutomationValue => {
  if (!value) {
    return null;
  }

  try {
    const parsedValue: unknown = JSON.parse(value);

    if (typeof parsedValue === "string") {
      return parsedValue;
    }

    if (
      Array.isArray(parsedValue) &&
      parsedValue.every((item) => typeof item === "string")
    ) {
      return parsedValue;
    }

    if (!isRecord(parsedValue)) {
      return null;
    }

    const direction = getRecordValue(parsedValue, ["direction", "Direction"]);
    const days = getRecordValue(parsedValue, ["days", "Days"]);

    if (
      (direction === "before" || direction === "after") &&
      typeof days === "number"
    ) {
      return {
        direction,
        days,
      };
    }

    if (
      "mainStatusId" in parsedValue ||
      "subStatusId" in parsedValue ||
      "MainStatusId" in parsedValue ||
      "SubStatusId" in parsedValue
    ) {
      return {
        mainStatusId: getNullableString(
          getRecordValue(parsedValue, ["mainStatusId", "MainStatusId"]),
        ),
        subStatusId: getNullableString(
          getRecordValue(parsedValue, ["subStatusId", "SubStatusId"]),
        ),
      };
    }

    const userIds = getRecordValue(parsedValue, ["userIds", "UserIds"]);

    if (Array.isArray(userIds)) {
      return {
        userIds: userIds
          .map((item) => getNullableString(item))
          .filter((item): item is string | null => item !== undefined),
      };
    }

    const emailTemplateId = getRecordValue(parsedValue, [
      "emailTemplateId",
      "EmailTemplateId",
    ]);

    if (
      Array.isArray(emailTemplateId) ||
      typeof emailTemplateId === "string" ||
      emailTemplateId === null
    ) {
      const emailAddress = getRecordValue(parsedValue, [
        "emailAddress",
        "EmailAddress",
      ]);

      return {
        emailTemplateId: Array.isArray(emailTemplateId)
          ? emailTemplateId.find(
              (item): item is string => typeof item === "string",
            ) ?? null
          : typeof emailTemplateId === "string"
          ? emailTemplateId
          : null,
        emailAddress: getNullableString(emailAddress) ?? null,
      };
    }
  } catch {
    return value;
  }

  return value;
};

export const parseAutomationConditionValue = (
  value: AutomationSerializedValue | undefined,
): AutomationConditionValue => {
  return parseAutomationValue(value);
};

export const parseAutomationActionValue = (
  value: AutomationSerializedValue | undefined,
): AutomationActionValue => {
  const parsedValue = parseAutomationValue(value);

  if (isDateValue(parsedValue)) {
    return null;
  }

  return parsedValue;
};

export const isStatusValue = (
  value: AutomationConditionValue | undefined,
): value is AutomationStatusData => {
  return (
    !!value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    ("mainStatusId" in value || "subStatusId" in value)
  );
};

export const isAssigneeValue = (
  value: AutomationConditionValue | AutomationActionValue | undefined,
): value is AutomationAssigneeData => {
  return (
    !!value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    "userIds" in value &&
    Array.isArray(value.userIds)
  );
};

export const isTemplateValue = (
  value: AutomationConditionValue | AutomationActionValue | undefined,
): value is AutomationTemplateData => {
  return (
    !!value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    "emailTemplateId" in value
  );
};

export const isDateValue = (
  value: AutomationConditionValue | undefined,
): value is AutomationDateConditionValue => {
  return (
    !!value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    "direction" in value &&
    "days" in value
  );
};

export const getStatusValueId = (
  value: AutomationConditionValue | undefined,
): string | undefined => {
  if (typeof value === "string") {
    return value;
  }

  if (!isStatusValue(value)) {
    return undefined;
  }

  return value.subStatusId || value.mainStatusId || undefined;
};

const getFirstDefinedId = (ids: Array<string | null | undefined>) => {
  return ids.find((item): item is string => Boolean(item));
};

export const getAssigneeValueId = (
  value: AutomationConditionValue | AutomationActionValue | undefined,
): string | undefined => {
  if (!isAssigneeValue(value)) {
    return undefined;
  }

  return getFirstDefinedId(value.userIds);
};

export const getTemplateValueId = (
  value: AutomationConditionValue | AutomationActionValue | undefined,
): string | undefined => {
  if (!isTemplateValue(value) || !value.emailTemplateId) {
    return undefined;
  }

  return value.emailTemplateId;
};

export const getTemplateEmailAddress = (
  value: AutomationConditionValue | AutomationActionValue | undefined,
): string | undefined => {
  if (!isTemplateValue(value) || !value.emailAddress) {
    return undefined;
  }

  return value.emailAddress;
};

export const getDateValueDays = (
  value: AutomationConditionValue | undefined,
): string | undefined => {
  if (typeof value === "string") {
    return value;
  }

  if (!isDateValue(value)) {
    return undefined;
  }

  return String(value.days);
};

export const getDateValueDirection = (
  value: AutomationConditionValue | undefined,
  fallback?: string,
): string | undefined => {
  if (isDateValue(value)) {
    return value.direction;
  }

  return fallback;
};

export const resolveAutomationValueLabel = (
  options: AutomationLookupOptions,
  optionsKey: AutomationOptionsKey,
  value: AutomationConditionValue | undefined,
  fallback = "",
): string => {
  if (Array.isArray(value)) {
    const labels = value.map((item) =>
      resolveOptionLabel(options, optionsKey, item, ""),
    );

    return labels.filter(Boolean).join(", ") || fallback;
  }

  if (isStatusValue(value)) {
    return resolveOptionLabel(
      options,
      optionsKey,
      getStatusValueId(value),
      fallback,
    );
  }

  if (isAssigneeValue(value)) {
    const labels = value.userIds.map((item) =>
      resolveOptionLabel(options, optionsKey, item ?? undefined, ""),
    );

    return labels.filter(Boolean).join(", ") || fallback;
  }

  if (isTemplateValue(value)) {
    const label = resolveOptionLabel(
      options,
      optionsKey,
      value.emailTemplateId ?? undefined,
      "",
    );

    return label || fallback;
  }

  if (isDateValue(value)) {
    return `${value.days}`;
  }

  return resolveOptionLabel(options, optionsKey, value ?? undefined, fallback);
};

export const getAutomationConditionBodyValue = ({
  conditionKey,
  conditionValue,
  conditionExtraValue,
  workStatusOptions,
  subtaskStatusOptions,
}: {
  conditionKey?: AutomationConditionKey;
  conditionValue?: string;
  conditionExtraValue?: string;
  workStatusOptions: SelectOptionType[];
  subtaskStatusOptions: SelectOptionType[];
}): AutomationConditionValue => {
  if (conditionKey === AUTOMATION_CONDITION_KEY.WORK_STATUS) {
    return getStatusConditionValue(workStatusOptions, conditionValue);
  }

  if (
    conditionKey === AUTOMATION_CONDITION_KEY.PREVIOUS_SUBTASK_STATUS ||
    conditionKey === AUTOMATION_CONDITION_KEY.CURRENT_SUBTASK_STATUS
  ) {
    return getStatusConditionValue(subtaskStatusOptions, conditionValue);
  }

  if (conditionKey === AUTOMATION_CONDITION_KEY.WORK_REACHES_DATE) {
    return getDateConditionValue(conditionValue, conditionExtraValue);
  }

  return conditionValue || null;
};

export const getAutomationActionBodyValue = ({
  actionKey,
  actionValue,
  actionExtraValue,
  workStatusOptions,
  subtaskStatusOptions,
}: {
  actionKey?: AutomationActionKey;
  actionValue?: string;
  actionExtraValue?: string;
  workStatusOptions: SelectOptionType[];
  subtaskStatusOptions: SelectOptionType[];
}): AutomationActionValue => {
  if (actionKey === AUTOMATION_ACTION_KEY.STATUS) {
    return getStatusConditionValue(workStatusOptions, actionValue);
  }

  if (actionKey === AUTOMATION_ACTION_KEY.CHANGE_STATUS) {
    return getStatusConditionValue(subtaskStatusOptions, actionValue);
  }

  if (
    actionKey === AUTOMATION_ACTION_KEY.ASSIGNEE ||
    actionKey === AUTOMATION_ACTION_KEY.SEND_NOTIFICATION
  ) {
    return getAssigneeData(actionValue);
  }

  if (actionKey === AUTOMATION_ACTION_KEY.SEND_EMAIL) {
    return getTemplateData({
      templateId: actionValue,
      emailAddress: actionExtraValue,
    });
  }

  return actionValue || null;
};

export const renderConditionSummary = (
  condition: ConditionConfig,
  options: AutomationLookupOptions,
  value?: AutomationConditionValue,
  extraValue?: string,
  fallback = "",
) => {
  if (condition.control.type === "none") {
    return condition.renderSummary();
  }

  if (condition.control.type === "dateOffset") {
    const direction = getDateValueDirection(value, extraValue);

    return condition.renderSummary(
      getDateValueDays(value) || fallback,
      resolveOptionLabel(
        options,
        condition.control.optionsKey,
        direction,
        fallback,
      ),
    );
  }

  return condition.renderSummary(
    resolveAutomationValueLabel(
      options,
      condition.control.optionsKey,
      value,
      fallback,
    ),
  );
};

export const renderActionSummary = (
  action: ActionConfig,
  options: AutomationLookupOptions,
  value?: AutomationActionValue,
  fallback = "",
) => {
  if (action.control.type === "none") {
    return action.renderSummary();
  }

  const optionsKey = getControlOptionsKey(action.control);

  if (!optionsKey) {
    const resolvedValue =
      typeof value === "string"
        ? value
        : resolveAutomationValueLabel(options, "users", value, fallback);

    return action.renderSummary(resolvedValue);
  }

  return action.renderSummary(
    resolveAutomationValueLabel(options, optionsKey, value, fallback),
  );
};
